/**
 * Cliente HTTP das certificações pendentes e da assinatura (E52.F3, tela T3).
 *
 * Espelha os contratos do backend (`certificacao_fila.py` e
 * `certificacao_assinatura.py`). Nada é recalculado aqui: prazo,
 * atraso, "vence hoje" e os três indicadores do topo chegam prontos do
 * servidor (BR-10 — nenhum número nasce na UI).
 *
 * O erro é DISCRIMINADO por razão estável porque a tela precisa dizer coisas
 * diferentes: 409 de disputa ("outro certificador está com este item") não é a
 * mesma frase de 422 de checklist ("a revisão precisa estar completa"), e um
 * "erro genérico" nos dois casos esconderia justamente a informação que
 * resolve a situação para quem está na tela.
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

import type { Modalidade } from '@/lib/cases';

export type StatusCertificacao = 'requerida' | 'em_revisao' | 'laudo_entregue';
export type AmbienteRequisicao = 'escritorio' | 'banco';
export type ResultadoExameCcb = 'favoravel' | 'inconclusivo' | 'desfavoravel' | 'em_exame';

export interface CertificacaoPendente {
  requisicao_id: string;
  case_id: string;
  case_display_code: string;
  source_document_id: string;
  ccb_number: string | null;
  escritorio_nome: string | null;
  resultado_exame: ResultadoExameCcb;
  /** E60.F3 — o caso furou a ordem do prazo; a tela precisa dizer isso. */
  prioritario?: boolean;
  /**
   * E74.F3 (ADR-0075 §8): o checklist tem os MESMOS três pontos nas duas
   * modalidades — conferi a metodologia, as evidências, o texto. Só o **rótulo**
   * do segundo muda: no eletrônico não há comparativo de traço a validar.
   */
  modalidade?: Modalidade;
  requerida_por: string;
  ambiente: AmbienteRequisicao;
  producao_origem: string | null;
  status: StatusCertificacao;
  requested_at: string;
  sla_due_at: string;
  atrasado: boolean;
  vence_hoje: boolean;
}

export interface CertificacaoIndicadores {
  na_fila: number;
  vencem_hoje: number;
  assinados_hoje: number;
}

export interface CertificacoesPendentes {
  itens: CertificacaoPendente[];
  indicadores: CertificacaoIndicadores;
}

export interface ChecklistRevisao {
  metodologia: boolean;
  comparativos: boolean;
  texto: boolean;
}

export interface RequisicaoAssumida {
  requisicao_id: string;
  source_document_id: string;
  status: 'em_revisao';
  certificador_email: string;
  assumido_em: string;
}

export interface AssinaturaLaudo {
  id: string;
  source_document_id: string;
  certificador_email: string;
  certificador_nome: string | null;
  certificador_registro: string | null;
  mesmo_autor_producao: boolean;
  documento_sha256: string;
  padrao: 'interna_registrada' | 'icp_brasil';
  assinado_em: string;
}

export type CertificacaoErrorReason =
  | 'disputada'
  | 'nao_assumida'
  | 'revisao_incompleta'
  | 'ja_assinado'
  | 'laudo_nao_pronto'
  | 'nao_encontrado'
  | 'sem_permissao'
  | 'rede'
  | 'desconhecido';

export class CertificacaoError extends Error {
  public readonly reason: CertificacaoErrorReason;

  constructor(reason: CertificacaoErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'CertificacaoError';
  }
}

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;

/** `detail` do backend → razão. É o código que distingue os dois 409 e os dois 422. */
const DETAIL_TO_REASON: Record<string, CertificacaoErrorReason> = {
  CERTIFICACAO_DISPUTADA: 'disputada',
  REQUISICAO_NAO_ASSUMIDA: 'nao_assumida',
  LAUDO_JA_ASSINADO: 'ja_assinado',
  REVISAO_INCOMPLETA: 'revisao_incompleta',
  LAUDO_NAO_PRONTO: 'laudo_nao_pronto',
};

function detailCode(err: AxiosError): string | null {
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return typeof detail === 'string' ? detail : null;
}

function reasonFor(err: AxiosError): CertificacaoErrorReason {
  const code = detailCode(err);
  if (code !== null) {
    const byDetail = DETAIL_TO_REASON[code];
    if (byDetail !== undefined) return byDetail;
  }
  const httpStatus = err.response?.status;
  if (httpStatus === HTTP_UNAUTHORIZED || httpStatus === HTTP_FORBIDDEN) return 'sem_permissao';
  if (httpStatus === HTTP_NOT_FOUND) return 'nao_encontrado';
  if (httpStatus === HTTP_CONFLICT) return 'ja_assinado';
  if (httpStatus === HTTP_UNPROCESSABLE) return 'revisao_incompleta';
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return 'rede';
  return 'desconhecido';
}

function classify(err: unknown): CertificacaoError {
  if (!axios.isAxiosError(err)) return new CertificacaoError('desconhecido');
  return new CertificacaoError(reasonFor(err as AxiosError));
}

export async function getPendentes(client: AxiosInstance): Promise<CertificacoesPendentes> {
  try {
    // Caminho do contrato do servidor (E52.F1), não texto de tela.
    const { data } = await client.get<CertificacoesPendentes>(
      '/certificacao/fila', // ux-neutrality:allow contrato-servidor
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}

export async function assumirRequisicao(
  client: AxiosInstance,
  requisicaoId: string,
): Promise<RequisicaoAssumida> {
  try {
    const { data } = await client.post<RequisicaoAssumida>(
      `/certificacao/requisicoes/${requisicaoId}/assumir`,
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}

export async function assinarLaudo(
  client: AxiosInstance,
  requisicaoId: string,
  checklist: ChecklistRevisao,
): Promise<AssinaturaLaudo> {
  try {
    const { data } = await client.post<AssinaturaLaudo>(
      `/certificacao/requisicoes/${requisicaoId}/assinar`,
      { checklist },
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}
